// @flow
import React from 'react'
import { FlexGrid } from 'wijmo/wijmo.grid'
import { CollectionView } from 'wijmo/wijmo'
import deepEqual from 'deep-equal'
import clone from 'clone-deep';
import ApiRequest from '../utils/api-request'
import type {
  ColumnsMap,
  GroupPanelsDefinition,
  SaveStatus,
  SortingDefinition,
  UserViewState,
} from '../types'
import {
  compareSortDescriptions,
  resetAndPopulateGroupPanels,
  resetAndPopulateSortDescriptions,
  applyCollapsedGroupLevels,
  getCollapsedGroupLevels,
} from '../utils'
import logger from '../utils/logger'
import PerfTimer from '../utils/perfTimer'
import { HolidaysContext } from '../context/holidays.context';

type GridODataStatus = 'initial-loading' | 'loading' | 'loaded'

type Props = {
  active: boolean,
  entity: string, // TODO: should be narrowed to conditional literals,
  selectedItems: number,
  frozenColumns: number,
  groups: Array<string>,
  sorting: Array<?{
    property: string,
    ascending: boolean,
  }>,
  defaultGroupPanels: GroupPanelsDefinition,
  defaultSorting: SortingDefinition,
  defaultColumns: ColumnsMap,
  columnsMapping: ColumnsMap,
  filterDefinition?: string,
  collapsedGroups?: Array<number>,
  loading: boolean,
  isMobile: Boolean,
  children: ({
               collection: any,
               status: GridODataStatus,
               isLoading: boolean,
               onGridInitialized: (FlexGrid) => void,
               onRowSelected: (any) => mixed,
               onViewSaved: (SaveStatus, UserViewState) => typeof undefined,
               onClearSelection: () => mixed,
               onGroupPanelChanged: (any) => mixed,
               onColSorted: (any) => mixed,
               onColResized: (any) => mixed,
               onColDragged: (any) => mixed,
               onColFreezed: (any) => mixed,
               onRowFreezed: (any) => mixed,
               onViewReset: () => mixed,
               onApplyViewSettings: (viewSettings: Object) => mixed,
               saveStatus: SaveStatus,
               userViewState: UserViewState,
               hasAllColumnsHidden: boolean,
               frozenColumns: number,
             }) => React.Node,
}

type State = {
  filters: any,
  status: GridODataStatus,
  selectedItems: number,
  saveStatus: SaveStatus,
  userViewState: UserViewState,
  hasAllColumnsHidden: boolean,
  frozenColumns: number,
  groups: Array<string>,
  withCustomMapping: boolean,
  columnsMapping: ColumnsMap,
  hasDataToDisplay: boolean,
  actionFilters: any,
  sorting: Array<?{
    property: string,
    ascending: boolean,
  }>,
  playResetAnimation: boolean,
  internalFilters: any,
  exportProgress: number | null,
  isCollapseLoading: boolean,
}

export default class GridFlaskController extends React.Component<Props, State> {
  static defaultProps = {
    isMobile: false,
  }

  static contextType = HolidaysContext;

  constructor(props) {
    super(props)

    this.state = {
      status: '',
      selectedItems: 0,
      saveStatus: '',
      userViewState: 'default',
      hasAllColumnsHidden: false,
      frozenColumns: 0,
      hasDataToDisplay: false,
      groups: [],
      sorting: null,
      columnsMapping: [],
      filters: '',
      withCustomMapping: false,
      playResetAnimation: false,
      internalFilters: false,
      actionFilters: {},
      multiColumnSort: false,
      exportProgress: null,
      isCollapseLoading: false,
    }

    this.collection = new CollectionView([])
    this.collection.refreshOnEdit = true

    // receiving from GridOData after initialise
    this.flexGridRef = null
    this.gridFilter = null
    
    // Store pending filter definition for when grid filter isn't ready yet
    // This handles the race condition where applyGridFilter is called before
    // the GridFlask component mounts and sets the gridFilter reference
    this.pendingFilterDefinition = null

    // these are cached default settings
    this.groupsDefault = props.defaultGroupPanels
    this.sortingDefault = props.defaultSorting
    this.columnsMappingDefault = this.getColumnMapping(props.defaultColumns)
    this.filtersDefault = ''
    this.frozenColumnsDefault = 0

    // these are cached user-saved settings
    this.groups = props.groups
    this.sorting = props.sorting
    this.columnsMapping = this.getColumnMapping(props.columnsMapping)
    this.filters = props.filterDefinition
    this.frozenColumns = props.frozenColumns
    this.collapsedGroups = props.collapsedGroups || []

    // Cache of last known non-empty collapsed levels.
    // Survives zero-row filter states (e.g., Orders "Samples" button removes
    // all rows, so getCollapsedGroupLevels returns [] because there are no
    // GroupRows to read). When switching back to a filter with rows, we
    // restore from this cache instead of losing the collapsed state.
    this._lastKnownCollapsedLevels = []
  }

  /**
   * Extract the sorting array from defaultSorting prop.
   * Handles both formats:
   * - Direct array: [{property, ascending}, ...]
   * - Object with sorting property: { sorting: [{property, ascending}, ...] }
   * 
   * This ensures consistency regardless of how defaultSorting is provided
   * (getDefaultUserSettings returns array, but some code expects object).
   * 
   * @returns {Array} - The sorting array, or empty array if not available
   */
  getDefaultSortingArray = (): Array<any> => {
    const defaultSorting = this.props.defaultSorting;
    if (!defaultSorting) {
      return [];
    }
    // Handle direct array format (from getDefaultUserSettings)
    if (Array.isArray(defaultSorting)) {
      return defaultSorting;
    }
    // Handle object with .sorting property
    if (defaultSorting.sorting && Array.isArray(defaultSorting.sorting)) {
      return defaultSorting.sorting;
    }
    return [];
  }

  /**
   * Extract the groups array from defaultGroupPanels prop.
   * Handles both formats:
   * - Direct array: ['field1', 'field2', ...]
   * - Object with groups property: { groups: ['field1', 'field2', ...] }
   * 
   * @returns {Array} - The groups array, or empty array if not available
   */
  getDefaultGroupsArray = (): Array<any> => {
    const defaultGroupPanels = this.props.defaultGroupPanels;
    if (!defaultGroupPanels) {
      return [];
    }
    // Handle direct array format
    if (Array.isArray(defaultGroupPanels)) {
      return defaultGroupPanels;
    }
    // Handle object with .groups property
    if (defaultGroupPanels.groups && Array.isArray(defaultGroupPanels.groups)) {
      return defaultGroupPanels.groups;
    }
    return [];
  }

  /**
   * Capture collapsed group levels with fallback to cached levels
   * when the grid has zero GroupRows (e.g., filter removed all data).
   * 
   * Three cases:
   * 1. Some rows collapsed → cache and return levels
   * 2. Zero GroupRows (empty filter result) → return cached levels  
   * 3. GroupRows exist but all expanded → cache empty and return empty
   * 
   * This solves the Orders "Samples" button problem: clicking Samples removes
   * all rows, so getCollapsedGroupLevels returns []. Without the cache,
   * switching back to "All Orders" would lose the previously collapsed state.
   */
  _captureCollapsedLevels = () => {
    if (!this.state.grid) return this._lastKnownCollapsedLevels || [];

    const levels = getCollapsedGroupLevels(this.state.grid);

    if (levels.length > 0) {
      // Got real collapsed levels - cache them
      this._lastKnownCollapsedLevels = levels;
      return levels;
    }

    // Empty result - determine why: zero rows or genuinely expanded?
    let hasGroupRows = false;
    if (this.state.grid.rows) {
      for (let i = 0; i < this.state.grid.rows.length; i++) {
        if (this.state.grid.rows[i].hasChildren) {
          hasGroupRows = true;
          break;
        }
      }
    }

    if (!hasGroupRows) {
      // Zero GroupRows (filter removed all data) - fall back to cache
      return this._lastKnownCollapsedLevels || [];
    }

    // Grid has GroupRows but none are collapsed - genuinely expanded
    this._lastKnownCollapsedLevels = [];
    return [];
  }

  async handleLoading() {
    this.setState((state) => {
      if (state.status === 'initial-loading' || state.status === 'loading') {
        return null
      }
      return {
        status: 'loading',
      }
    })
  }

  async handleLoadingInitial() {
    this.setState((state) => {
      if (state.status === 'initial-loading') {
        return null
      }
      return {
        status: 'initial-loading',
      }
    })
  }

  async handleLoadedInitial() {
    this.setState((state) => {
      if (state.status === 'initial-loaded') {
        return null
      }
      return {
        status: 'loading',
      }
    })
  }

  async handleLoaded() {
    this.setState((state) => {
      if (state.status === 'initial-loading' || state.status === 'loaded') {
        return null
      }
      return {
        status: 'loaded',
        hasDataToDisplay: this.collection.totalItemCount > 0,
      }
    })
  }

  /**
   * Force set status to 'loaded' regardless of current state.
   * Used after view switching to ensure buttons are re-enabled.
   * Does NOT recalculate hasDataToDisplay since the underlying data
   * hasn't changed - only the view settings have changed.
   * Note: When applying groupings, the collection may temporarily report
   * 0 items, so we preserve the existing hasDataToDisplay value.
   */
  forceLoaded() {
    this.setState({
      status: 'loaded',
    })
  }

  /**
   * Recalculate hasDataToDisplay based on current collection state.
   * Used after operations that change the visible data (like clearing filters)
   * without triggering a full data reload.
   * Unlike forceLoaded(), this DOES recalculate hasDataToDisplay because
   * the filter change affects which rows are visible.
   */
  recalculateHasDataToDisplay = () => {
    this.setState({
      hasDataToDisplay: this.collection.totalItemCount > 0,
    })
  }

  /**
   * Set the collapse/expand loading state.
   * Used to show a loading indicator during blocking collapse/expand operations.
   * @param {boolean} isLoading - Whether the collapse/expand operation is in progress
   */
  setCollapseLoading = (isLoading: boolean) => {
    this.setState({
      isCollapseLoading: isLoading,
    })
  }

  /**
   * Hide loading indicator after the grid has finished rendering.
   * Uses double requestAnimationFrame to ensure we're after the paint cycle.
   * 
   * TIMING FIX: Added setTimeout(150) to account for the deferred collapsed
   * state application (100ms). This ensures the loading indicator stays visible
   * until after the collapsed state has been applied.
   */
  hideLoadingWhenReady = () => {
    // Wait for deferred collapse operation to complete (100ms) plus buffer
    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setCollapseLoading(false);
          this.forceLoaded();
        });
      });
    }, 150);
  }

  /**
   * Dismiss the loading overlay after the browser has painted.
   * Uses double requestAnimationFrame to ensure we wait for the paint cycle
   * to complete after a blocking DOM operation (like collapse/expand).
   * 
   * Unlike hideLoadingWhenReady(), this has NO built-in delay — it expects
   * to be called at the right moment (i.e., after the blocking work is done).
   */
  dismissLoadingAfterPaint = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setCollapseLoading(false);
        this.forceLoaded();
      });
    });
  }

  /**
   * Restore collapsed group levels after a collection refresh.
   * 
   * Called after filter changes or data refetches that cause Wijmo to rebuild
   * GroupRow objects. The caller should have already reset _collapseLevel to 100
   * via grid.collapseGroupsToLevel(100) BEFORE the operation, so new GroupRows
   * are created expanded by default.
   * 
   * This method only needs to selectively re-collapse specific levels (if any
   * were captured before the operation). If collapsedLevels is empty, the rows
   * are already expanded from the pre-reset and we just update button state.
   * 
   * @param {Array<number>} collapsedLevels - Previously captured collapsed levels
   */
  restoreCollapsedState = (collapsedLevels) => {
    if (!this.state.grid) return;

    const grid = this.state.grid;

    // Wait for Wijmo to finish rebuilding GroupRows after the collection change
    setTimeout(() => {
      if (collapsedLevels && collapsedLevels.length > 0) {
        applyCollapsedGroupLevels(grid, collapsedLevels);
      }
      // If collapsedLevels is empty, rows are already expanded from the
      // pre-reset of _collapseLevel to 100 before the filter operation.
      // Just update button states.
      this.onGroupCollapsedChanged();
    }, 200);
  }

  onExporting = async (status, cb) => {
    this.setState({ status }, cb)
  }

  onExported = async (_grid, _filename) => {
    await this.handleLoaded()
  }

  getColumnMapping(mapping) {
    const result = mapping
    if (Array.isArray(result) && result.length > 0) {
      result.forEach((column) => {
        if (column.header === 'Cases' ||
          column.header === 'Total' ||
          column.header === 'Total $' ||
          column.header === 'Quantity' ||
          column.header === 'Liters' ||
          column.header === '9L/equiv.' ||
          column.header === 'Gallons' ||
          column.header === 'MTD' ||
          column.header === 'LY MTD' ||
          column.header === 'MTD $ Diff' ||
          column.header === 'YTD' ||
          column.header === 'LY YTD' ||
          column.header === 'YTD $ Diff' ||
          column.header === "Cost"
        ) {
          column.aggregate = 'Sum'
        } else {
          column.aggregate = 'None'
        }

        if (column.header === 'Order Date' ||
          column.header === 'Ship Date' ||
          column.header === 'Exp Date' ||
          column.header === 'Receipt Date' ||
          column.header === 'Last Invoice Date' ||
          column.header === 'BO ETA'
        ) {
          column.dataType = 'Date'
        }
      })

      return result
    }
  }

  async getCollectionData() {
    const { entity, flattenFunction } = this.props

    try {
      PerfTimer.getInstance().restart()
      PerfTimer.getInstance().print('started fetch')
      const { data } = await ApiRequest.get(entity)

      PerfTimer.getInstance().print('started flattening')
      this.collection.sourceCollection = await flattenFunction(data)
    } catch (error) {
      logger.error(`Error loading ${entity} data ${error}`)
    }
  }

  async handleSetViewToDefault() {
    this.handleSetGrouping(this.groupsDefault)
    await this.setColumns(this.columnsMappingDefault)
    this.handleSetSorting(this.sortingDefault)
    this.handleSetFilters(this.filtersDefault)
    this.applyGridFilter(this.filtersDefault)
    this.handleFrozenCol(this.frozenColumnsDefault)
    
    // Reset collapsed state - expand all groups for default view
    // Note: We don't expand here because onViewReset handles it after endUpdate
  }

  async handleSetViewToUserSettings() {
    this.handleSetGrouping(this.groups)
    await this.setColumns(this.columnsMapping)
    this.handleSetSorting(this.sorting)
    this.handleSetFilters(this.filters)
    this.applyGridFilter(this.filters)
    this.handleFrozenCol(this.frozenColumns)
    
    // Apply saved collapsed state if available
    // TIMING FIX: Defer until after Wijmo finishes rebuilding GroupRow objects
    // after the grouping changes trigger an async grid refresh.
    if (this.collapsedGroups && this.collapsedGroups.length > 0 && this.state.grid) {
      setTimeout(() => {
        applyCollapsedGroupLevels(this.state.grid, this.collapsedGroups);
        // Trigger re-render for button states
        this.onGroupCollapsedChanged();
      }, 100);
    }
  }

  // state of a component depends on changes of props
  oldDerivedStateFromProps(props: Props, state: State) {
    if (props.loading) return null
    if (!this || !this.collection) return null

    // Only allow props to override state during initial load (when userViewState is 'default').
    // Once the user has made any changes or loaded a view, the state is managed by
    // user actions and should not be overwritten by props. This prevents bugs where:
    // - Sorting reverts after "Save as New" (userViewState becomes 'saved')
    // - Settings revert after any modification (userViewState becomes 'custom' or 'def_custom')
    if (state.userViewState !== 'default') {
      return;
    }

    // FIX: Removed mobile guard that blocked sorting/grouping/filter setup on mobile
    // when saved views with custom columns existed.
    // Original code: if (this.props.isMobile && state.withCustomMapping) { return; }
    // This prevented proper initialization of sorting, grouping, and filters on mobile
    // when withCustomMapping was true (i.e., when columnsMapping prop was provided).
    // While the collection IS created in the constructor for Flask-backed modules,
    // this guard still blocked proper view setup, causing incorrect grid display.

    const collection = this.collection
    let updates = {}

    // Extract default sorting array using helper method that handles both formats
    const defaultSortingArray = this.getDefaultSortingArray();

    // Apply sorting: explicit props take precedence, then fall back to defaults
    if (
      Array.isArray(props.sorting) &&
      props.sorting.length > 0 &&
      !compareSortDescriptions(props.sorting, state.sorting)
    ) {
      resetAndPopulateSortDescriptions(props.sorting, collection)
      updates.sorting = props.sorting
    } else if (
      defaultSortingArray.length > 0 &&
      !compareSortDescriptions(state.sorting, defaultSortingArray)
    ) {
      // FIX: Use helper method to handle both array and object formats
      // Previously this checked props.defaultSorting.sorting which failed when
      // defaultSorting was already an array (from getDefaultUserSettings)
      updates.sorting = defaultSortingArray
      resetAndPopulateSortDescriptions(defaultSortingArray, collection)
    }

    if (
      props.filterDefinition &&
      !deepEqual(state.filters, props.filterDefinition)
    ) {
      updates.filters = props.filterDefinition
      collection.filterDefinition = props.filterDefinition
    }

    const hasUserCustomMapping =
      Array.isArray(props.columnsMapping) && props.columnsMapping.length > 0

    let result

    if (hasUserCustomMapping && !state.withCustomMapping) {
      result = {
        ...state,
        ...updates,
        withCustomMapping: true,
        userViewState: 'custom',
      }
    } else if (Object.keys(updates).length > 0) {
      result = {
        ...state,
        ...updates,
      }
    } else {
      result = null
    }

    this.setState(result);
  }

  async componentDidMount() {
    this.oldDerivedStateFromProps(this.props, this.state);
    await this.handleLoadingInitial()
    await this.handleRefetch()
    await this.handleLoadedInitial()
    await this.handleLoading()
    this.handleSetView()
    await this.handleLoaded()
    await this.setViewState('default')
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props !== prevProps) {
      this.oldDerivedStateFromProps(this.props, this.state);
    }
  }

  // Decides what settings to use
  // 1. Tries to set user-saved settings first
  // 2. If user settings are not available fallbacks
  // 3. Uses a default settings as the fallback
  handleSetView() {
    // Determine if user-saved settings are present, judge by non-null this.columns
    const useUserSaved = Array.isArray(this.columnsMapping) &&
      this.columnsMapping.length > 0

    useUserSaved ? this.handleSetViewToUserSettings() : this.handleSetViewToDefault()
  }


  handleRefetch = async () => {
    // Capture collapsed state before data refetch rebuilds GroupRow objects.
    // Uses _captureCollapsedLevels to survive zero-row filter states.
    const collapsedLevels = this._captureCollapsedLevels();

    // Reset Wijmo's internal _collapseLevel before the refetch operation.
    // This ensures new GroupRows created during the collection refresh
    // are created expanded by default, regardless of prior CollapseAction state.
    if (this.state.grid) {
      this.state.grid.collapseGroupsToLevel(100);
    }

    await this.handleLoading()
    await this.getCollectionData()

    if (this.props.filterExternal && Object.keys(this.state.actionFilters).length) {
      this.handleFilterAction(this.state.internalFilters)
      await this.handleLoaded()
      this.restoreCollapsedState(collapsedLevels);
      return
    }

    if (!this.props.filterExternal && !this.props.filterInternal) {
      this.handleFilterAction(this.state.internalFilters)
      await this.handleLoaded()
      this.restoreCollapsedState(collapsedLevels);
      return
    }

    this.collection.filter = null
    await this.handleLoaded()
    this.restoreCollapsedState(collapsedLevels);
  }

  setGrouping(groups: GroupPanelsDefinition) {
    this.setState((_) => ({
      groups: clone(groups),
    }))
  }

  onClearSelection = () => {
    this.setState({
      selectedItems: 0,
    })
  }


  // Next 3 functions are about filtering system in Wijmo. We have external and internal filters.
  // And we want to combine them in Invoices / Orders / Samples modules.
  // So user can press MTD/YTD... or Daily Sales/MOBO, and also apply internal filter from FlexGridFilter.
  // The problem was, that internal was overwriting external and vise versa.
  // Next functions is the logic how to filter using all filters together.
  // For other modules, without external filters — we don't use it.
  onInternalGridFiltersChanged = (gridRef, passedFilter) => {
    this.flexGridRef = gridRef

    this.setState(prevState => ({
      ...prevState,
      internalFilters: passedFilter
    }))

    this.handleFilterAction(true)
  }

  /**
   * Handle filter changes from action buttons (e.g., Daily Sales/MOBOs/Futures/Samples).
   * 
   * Uses the same deferred loading pattern as onApplyViewSettings and onViewReset
   * to prevent the browser from showing "Page Unresponsive" dialogs on modules with
   * large datasets.
   * 
   * COLLAPSED STATE PRESERVATION:
   * collapseGroupsToLevel(N) only affects GroupRows that CURRENTLY EXIST in the grid.
   * It does NOT make newly created GroupRows inherit the collapse level. This means:
   * - Filter transitions that REMOVE rows (e.g., All→Daily Sales) work with pre-set
   *   alone, because surviving GroupRows retain their collapsed state.
   * - Filter transitions that ADD rows (e.g., Daily Sales→All Orders) break, because
   *   new GroupRows for the added data are born expanded by default.
   * 
   * Solution: After the filter rebuilds GroupRows, explicitly apply collapsed levels
   * to ALL rows (including newly created ones) using applyCollapsedGroupLevels().
   * This is the same restoreCollapsedState pattern used by handleRefetch().
   */
  onFiltersChanged = async (filterType: string, filters: any) => {
    console.time('[FilterChange] Total');

    // Capture collapsed state before filter changes cause GroupRow rebuild.
    // Survives the async gap via closure.
    const collapsedLevels = this._captureCollapsedLevels();

    // Show loading indicator immediately
    this.setCollapseLoading(true);

    // Use requestAnimationFrame + setTimeout to ensure the browser has time to
    // repaint and show the loading indicator before the blocking operations begin.
    requestAnimationFrame(() => {
      setTimeout(async () => {
        try {
          console.time('[FilterChange] Apply filter');

          // Step 1: Update state FIRST — these trigger synchronous React re-renders
          // (in React 16 inside setTimeout, setState is NOT batched) which may cause
          // Wijmo to reset _collapseLevel. So we do them BEFORE the pre-set.
          this.setState(prevState => {
            return {
              ...prevState,
              actionFilters: {
                ...prevState.actionFilters,
                [filterType]: filters
              }
            }
          })

          await this.handleLoading()

          // Step 2: Pre-set _collapseLevel before the filter operation.
          // This helps for filter transitions that REMOVE rows (subset transitions)
          // where surviving GroupRows retain the collapse state set here.
          // However, this alone is NOT sufficient for transitions that ADD rows
          // (superset transitions) — new GroupRows ignore _collapseLevel.
          // Step 4 below handles those new rows explicitly.
          if (this.state.grid) {
            if (collapsedLevels && collapsedLevels.length > 0) {
              const targetLevel = Math.min(...collapsedLevels);
              this.state.grid.collapseGroupsToLevel(targetLevel);
              console.log('[FilterChange] Pre-set _collapseLevel to', targetLevel, 'from collapsedLevels:', collapsedLevels);
            } else {
              this.state.grid.collapseGroupsToLevel(100);
              console.log('[FilterChange] Pre-set _collapseLevel to 100 (all expanded)');
            }
          }

          // Step 3: Apply filter — this changes the collection, which triggers Wijmo
          // to rebuild GroupRow objects. Existing GroupRows retain their collapsed
          // state from Step 2, but NEW GroupRows are born expanded.
          this.handleFilterAction(this.state.internalFilters)
          await this.handleLoaded()

          console.timeEnd('[FilterChange] Apply filter');

          // Step 4: Restore collapsed state AFTER Wijmo has rebuilt GroupRows.
          // This is critical for filter transitions that ADD rows (e.g., switching
          // from "Daily Sales" back to "All Orders"). The pre-set in Step 2 only
          // affected rows that existed before the filter change. New GroupRows
          // created by the filter (for newly visible data) defaulted to expanded.
          // applyCollapsedGroupLevels iterates ALL rows including new ones.
          if (collapsedLevels && collapsedLevels.length > 0 && this.state.grid) {
            console.time('[FilterChange] Restore collapsed state');
            // Wait for Wijmo to finish rebuilding GroupRows after the filter change.
            // The collection filter change is synchronous, but Wijmo's internal
            // grid refresh that creates GroupRow objects is deferred.
            await new Promise(resolve => {
              setTimeout(() => {
                applyCollapsedGroupLevels(this.state.grid, collapsedLevels);
                console.timeEnd('[FilterChange] Restore collapsed state');
                resolve();
              }, 200);
            });
          }

          // Update Collapse/Expand button states.
          this.onGroupCollapsedChanged();

          console.timeEnd('[FilterChange] Total');
        } catch (e) {
          logger.error('[onFiltersChanged] Error applying filters:', e);
          console.timeEnd('[FilterChange] Total');
        } finally {
          // Dismiss loading overlay after the browser paints the final state.
          // This now runs AFTER the 200ms restore delay (Step 4), so the
          // loading indicator stays visible during the entire operation.
          this.dismissLoadingAfterPaint();
        }
      }, 50);
    });
  }

  // NOTE: here are check if hasInternal true. This is for an optimization.
  // The behaviour is different for cases, when we select external filter, without selecting internal.
  // When we have selected some internal filter, we will need to check every column filter applied, before applying external.
  // But, in scenario, when we don't select anything in internal filter, we can simply change our collection with externals,
  // not touching every column filter.
  handleFilterAction(hasInternal) {
    const { actionFilters, internalFilters } = this.state
    const { filterExternal, filterInternal } = this.props

    if (!filterExternal) return

    if (hasInternal) {
      this.flexGridRef &&
        this.flexGridRef.collectionView &&
        filterInternal(this.flexGridRef, internalFilters, actionFilters, this.context.holidays);
    } else {
      this.collection.filter = filterExternal(actionFilters, this.context.holidays)
    }
  }

  /**
   * Store reference to the FlexGridFilter instance.
   * Also applies any pending filter that was waiting for the grid to initialize.
   * This handles the race condition where applyGridFilter is called during
   * componentDidMount before the GridFlask component has mounted.
   */
  setFlexGridFilter = (filterInstance) => {
    this.gridFilter = filterInstance;
    
    // Apply any pending filter that was waiting for the grid to initialize
    // Only apply if there's actually a non-empty filter string
    if (this.pendingFilterDefinition && this.gridFilter) {
      try {
        this.gridFilter.filterDefinition = this.pendingFilterDefinition;
      } catch (e) {
        logger.error('[setFlexGridFilter] Error applying pending filter:', e);
      }
      this.pendingFilterDefinition = null;
    }
  }

  /**
   * Get the current filter definition from the FlexGridFilter component.
   * This captures all column filters that the user has applied via the UI.
   * @returns {string|null} The filter definition JSON string, or null if no filter is set
   */
  getGridFilterDefinition = () => {
    if (this.gridFilter && this.gridFilter.filterDefinition) {
      return this.gridFilter.filterDefinition;
    }
    return null;
  }

  /**
   * Clear the grid filter UI.
   * This is a helper method to ensure filters are properly cleared
   * when switching to views without filters.
   */
  clearGridFilter = () => {
    this.pendingFilterDefinition = null;
    if (this.gridFilter) {
      try {
        this.gridFilter.filterDefinition = null;
      } catch (e) {
        logger.error('[clearGridFilter] Error clearing filter:', e);
      }
    }
  }

  /**
   * Apply a filter definition to the FlexGridFilter component.
   * This restores the column filter UI state from a saved view.
   * 
   * IMPORTANT: This method ALWAYS clears existing filters when the new filter
   * is empty/null. This prevents the "conditional-clear antipattern" where
   * switching from a view WITH filters to a view WITHOUT filters would leave
   * the old filters in place.
   * 
   * Handles the race condition where this may be called before the grid
   * filter is initialized by storing the filter as pending. The pending
   * filter will be applied when setFlexGridFilter is called.
   * 
   * Also handles the case where filterDefinition comes from GraphQL as an
   * object with numeric keys instead of a string (Wijmo requires a string).
   * 
   * @param {string|object} filterDefinition - The filter definition to apply
   */
  applyGridFilter = (filterDefinition) => {
    // Handle null/undefined/empty string - ALWAYS clear the grid filter
    // This fixes the conditional-clear antipattern where switching from a view
    // with filters to a view without filters would leave the old filters in place
    if (!filterDefinition) {
      this.clearGridFilter();
      return;
    }

    // Ensure filterDefinition is a string - it may come as an object from GraphQL
    // where each character is stored as a separate property with numeric keys
    let filterString = filterDefinition;
    if (filterDefinition && typeof filterDefinition === 'object') {
      // If it's an array-like object with numeric keys, join the characters
      if (filterDefinition[0] !== undefined) {
        filterString = Object.values(filterDefinition).join('');
      } else {
        // Otherwise try to stringify it
        filterString = JSON.stringify(filterDefinition);
      }
    }
    
    // Check if the filter actually contains any filters to apply
    // If it's just an empty default like {"defaultFilterType":3,"filters":[]},
    // we should CLEAR the existing filter, not skip
    if (filterString) {
      try {
        const parsed = JSON.parse(filterString);
        if (!parsed.filters || parsed.filters.length === 0) {
          // Empty filters array - clear existing filters
          this.clearGridFilter();
          return;
        }
      } catch (e) {
        // If parsing fails, continue with applying (might be a different format)
      }
    }
    
    // Store the filter definition in case grid isn't ready yet
    this.pendingFilterDefinition = filterString || null;
    
    // Use setTimeout to ensure the grid filter is ready (for cases where
    // the grid is already initialized but we're in a render cycle)
    setTimeout(() => {
      if (this.gridFilter) {
        try {
          // Apply new filter if provided (no need to clear first - setting replaces)
          if (filterString) {
            this.gridFilter.filterDefinition = filterString;
          }
          // Clear pending since we successfully applied
          this.pendingFilterDefinition = null;
        } catch (e) {
          logger.error('[applyGridFilter] Error applying filter:', e);
        }
      }
      // If gridFilter doesn't exist yet, pendingFilterDefinition will be applied
      // when setFlexGridFilter is called by the GridFlask component
    }, 0);
  }

  clearInternalFilters = async () => {
    this.handleResetAnimation()
    setTimeout(() => {
      if (!this.state.internalFilters) return;
      this.gridFilter.filterDefinition = null;
      // Recalculate hasDataToDisplay after filter is cleared.
      // Use another setTimeout to ensure the collection has updated
      // after the filter definition change.
      setTimeout(() => {
        this.recalculateHasDataToDisplay()
      }, 0)
    }, 0)
  }

  onRowSelected = (gridInstance: any) => {
    const { selectedItems } = gridInstance

    if (selectedItems.length !== 0) {
      if (this.rowSelectedTimer) {
        clearTimeout(this.rowSelectedTimer)
      }

      this.rowSelectedTimer = setTimeout(() => {
        // TODO: can't remember the reason we needed this
        // if (this.unmounted || !this.props.active) return;

        this.setState(() => {
          return {
            selectedItems: selectedItems.length,
            currentItem: this.collection && this.collection.currentItem,
          }
        })
      }, 25)
    }
  }

  onViewSaved = (status: SaveStatus, viewState: UserViewState) => {
    this.setState({
      saveStatus: status || 'saved',
      userViewState: viewState === 'def_custom' ? 'custom' : viewState,
    })

    // only clean up the `saveStatus` when there isn't an error in place.
    if (status !== 'error') {
      this.errorTimer = setTimeout(() => {
        this.setState({
          saveStatus: '',
        })
      }, 1000)
    }
  }

  async handleViewChanged() {
    const newViewState = this.getViewState() === 'default' || this.getViewState().indexOf('def_') !== -1
      ? 'def_custom'
      : 'custom'
    this.setViewState(newViewState)
    this.setViewSavedState('unsaved')
  }

  handleFrozenCol(colNumber) {
    this.setState(_ => ({
      frozenColumns: colNumber,
    }))
  }

  onColumnsMappingChange = async (changedColumns: ColumnsMap) => {
    const columnsMapping = this.state.columnsMapping.map((col) => {
      const { binding } = col
      const changedCol = changedColumns.find((i) => i.binding === binding)

      if (changedCol) {
        // we can't use object spread in here since if we do it none
        // of the properties from col would be inherited.
        // eslint-disable-next-line
        col.visible = changedCol.visible

        return col
      }

      return col
    })

    await this.setColumns(columnsMapping)

    const hasAllColumnsHidden = !columnsMapping.some(
      (col) => col.visible === true,
    )

    await this.setState(_ => ({
      hasAllColumnsHidden,
    }))

    await this.handleViewChanged()
  }

  onColSorted = (newSortings: SortingDefinition) => {
    this.setSorting(newSortings)
    this.handleViewChanged()
  }

  onColResized = (newSize: ColumnsMap) => {
    this.setColumns(newSize)
    this.handleViewChanged()
  }

  onColDragged = (newMapping: ColumnsMap) => {
    this.setColumns(newMapping)
    this.handleViewChanged()
  }

  onGroupPanelChanged = (newGroups: GroupPanelsDefinition) => {
    this.setGrouping(newGroups)
    this.handleViewChanged()
  }

  onColFreezed = (colNumber: number) => {
    this.handleFrozenCol(colNumber)
    this.handleViewChanged()
  }

  onRowFreezed = (rowNumber: number) => {
    this.handleFrozenRow(rowNumber)
  }

  onGroupCollapsedChanged = (_groups: GroupPanelsDefinition) => {
    this.onClearSelection()
  }

  onGridInitialized = (grid: FlexGrid) => {
    PerfTimer.getInstance().print('grid initialized')
    this.setState({ grid })
  }

  handleSetGrouping = (newGroups) => {
    resetAndPopulateGroupPanels(newGroups, this.collection)
    this.setGrouping(newGroups)
  }

  handleSetSorting = (sorting) => {
    resetAndPopulateSortDescriptions(sorting, this.collection)
    this.setSorting(sorting)
  }

  handleSetFilters = (filterText) => {
    this.collection.filterDefinition = filterText
    this.setFilters(filterText)
  }

  setColumns = async (columns: ColumnsMap) => {
    this.setState(_ => ({
      columnsMapping: clone(columns),
    }))
  }

  setFilters = (filters) => {
    this.setState(_ => ({
      filters: clone(filters),
    }))
  }

  setSorting = (sorting) => {
    this.setState(_ => ({
      sorting: clone(sorting),
    }))
  }

  getViewState = (): SaveStatus => {
    return this.state.userViewState
  }

  getViewSavedState = (): UserViewState => {
    return this.state.saveStatus
  }

  setViewSavedState = async (newState: SaveStatus) => {
    this.setState(_ => ({
      saveStatus: newState,
    }))
  }

  setViewState = async (newState: UserViewState) => {
    this.setState(_ => ({
      userViewState: newState,
    }))
  }

  /**
   * Expand all groups in the grid using Wijmo's optimized batch method.
   * This is MUCH faster than iterating through rows individually.
   * 
   * collapseGroupsToLevel(n) collapses groups to level n, so using a very
   * high number effectively expands all groups.
   * 
   * @param {FlexGrid} grid - The Wijmo FlexGrid instance
   */
  expandAllGroups = (grid) => {
    if (!grid) return;
    
    try {
      // collapseGroupsToLevel with a high number expands all groups
      // This is a single batched operation, much faster than row iteration
      grid.collapseGroupsToLevel(100);
    } catch (e) {
      // Fallback to manual iteration if the method fails
      // (e.g., if Wijmo version doesn't support it)
      logger.warn('[expandAllGroups] collapseGroupsToLevel failed, using fallback:', e);
      if (grid.rows) {
        grid.rows.forEach(row => {
          if (row.hasChildren) {
            row.isCollapsed = false;
          }
        });
      }
    }
  }

  /**
   * Reset view to default settings.
   * Uses same deferred loading pattern as onApplyViewSettings for consistency
   * and to ensure loading indicator displays on large datasets.
   */
  onViewReset = async () => {
    console.time('[ViewReset] Total');
    
    // Show loading indicator immediately
    this.setCollapseLoading(true);
    
    // Use requestAnimationFrame + setTimeout to ensure the browser has time to
    // repaint and show the loading indicator before the blocking operations begin.
    requestAnimationFrame(() => {
      setTimeout(async () => {
        try {
          // Batch all collection changes to prevent multiple refreshes
          console.time('[ViewReset] Batch update');
          this.collection.beginUpdate();
          try {
            await this.handleSetViewToDefault()
          } finally {
            console.time('[ViewReset] - endUpdate (collection refresh)');
            this.collection.endUpdate();
            console.timeEnd('[ViewReset] - endUpdate (collection refresh)');
          }
          console.timeEnd('[ViewReset] Batch update');

          // Expand all groups using optimized method
          // TIMING FIX: Must defer until after Wijmo finishes rebuilding GroupRow objects.
          // endUpdate() triggers an async grid refresh - the GroupRow objects don't exist
          // until that refresh completes.
          console.time('[ViewReset] Expand all groups');
          if (this.state.grid) {
            setTimeout(() => {
              this.expandAllGroups(this.state.grid);
              // Trigger re-render for button states
              this.onGroupCollapsedChanged();
              console.timeEnd('[ViewReset] Expand all groups');
            }, 100);
          }

          const getSaveStatus: SaveStatus = () => {
            return (this.getViewState() === 'default' || this.getViewState() === 'def_custom') && this.getViewSavedState() !== 'unsaved'
              ? ''
              : 'reset'
          }

          await this.setViewSavedState(getSaveStatus())
          await this.setViewState('default')
          
          console.timeEnd('[ViewReset] Total');
        } catch (e) {
          logger.error('[onViewReset] Error resetting view:', e);
          console.timeEnd('[ViewReset] Total');
        } finally {
          // Hide loading indicator after grid finishes rendering
          this.hideLoadingWhenReady();
        }
      }, 50);
    });
  }

  /**
   * Apply view settings from a saved view object.
   * This allows switching between views without a server query.
   * 
   * Performance optimization: Uses beginUpdate()/endUpdate() to batch all
   * collection changes into a single refresh. This is critical for large
   * datasets (like Invoices) where each individual change would trigger
   * an expensive recalculation.
   * 
   * TIMING INSTRUMENTATION: Console logs are included to help identify
   * performance bottlenecks. Check browser console during view switches.
   * 
   * @param {Object} viewSettings - The view settings to apply
   * @param {Array} viewSettings.columns - Column definitions
   * @param {Array} viewSettings.sorting - Sort definitions
   * @param {Array} viewSettings.groups - Group panel definitions
   * @param {number} viewSettings.frozenColumns - Number of frozen columns
   * @param {string} viewSettings.filterDefinition - Filter definition JSON for column filters
   * @param {Array<number>} viewSettings.collapsedGroups - Group levels to collapse
   */
  onApplyViewSettings = async (viewSettings) => {
    console.time('[ViewSwitch] Total');
    
    // Show loading indicator immediately
    this.setCollapseLoading(true);
    
    // Use requestAnimationFrame + setTimeout to ensure the browser has time to
    // repaint and show the loading indicator before the blocking operations begin.
    // This is the same pattern used in CollapseAction/ExpandAction.
    requestAnimationFrame(() => {
      setTimeout(async () => {
        try {
          const groups = viewSettings.groups || [];
          const collapsedGroups = viewSettings.collapsedGroups || [];
          const sorting = viewSettings.sorting || [];
          const filterDefinition = viewSettings.filterDefinition || '';
          const columns = viewSettings.columns || [];
          const frozenColumns = viewSettings.frozenColumns || 0;
          
          // Batch all collection changes to prevent multiple refreshes
          // This is critical for performance on large datasets
          console.time('[ViewSwitch] Batch update (groups, sorting, filters)');
          this.collection.beginUpdate();
          
          try {
            // Apply groups
            console.time('[ViewSwitch] - Apply groups');
            resetAndPopulateGroupPanels(groups, this.collection);
            this.setGrouping(groups);
            console.timeEnd('[ViewSwitch] - Apply groups');

            // Apply sorting
            console.time('[ViewSwitch] - Apply sorting');
            resetAndPopulateSortDescriptions(sorting, this.collection);
            this.setSorting(sorting);
            console.timeEnd('[ViewSwitch] - Apply sorting');

            // Apply filters to collection
            console.time('[ViewSwitch] - Apply filters');
            this.collection.filterDefinition = filterDefinition;
            this.setFilters(filterDefinition);
            console.timeEnd('[ViewSwitch] - Apply filters');
          } finally {
            // End the batch update - this triggers a single refresh
            console.time('[ViewSwitch] - endUpdate (collection refresh)');
            this.collection.endUpdate();
            console.timeEnd('[ViewSwitch] - endUpdate (collection refresh)');
          }
          console.timeEnd('[ViewSwitch] Batch update (groups, sorting, filters)');

          // Apply columns (with processing for aggregates and data types)
          // This is a React state update, not a collection change
          console.time('[ViewSwitch] Apply columns (React state)');
          if (columns.length > 0) {
            var processedColumns = this.getColumnMapping(columns);
            if (processedColumns) {
              await this.setColumns(processedColumns)
            }
          } else {
            // No custom columns in view - fall back to default columns
            // FIX: Previously this called setColumns([]) which cleared all columns,
            // resulting in an empty grid. Now we use default columns instead.
            if (this.columnsMappingDefault && this.columnsMappingDefault.length > 0) {
              await this.setColumns(this.columnsMappingDefault)
            }
          }
          console.timeEnd('[ViewSwitch] Apply columns (React state)');

          // Apply grid filter UI (outside beginUpdate as it operates on the grid, not collection)
          // IMPORTANT: applyGridFilter now properly clears filters when filterDefinition is empty
          console.time('[ViewSwitch] Apply grid filter UI');
          this.applyGridFilter(filterDefinition);
          console.timeEnd('[ViewSwitch] Apply grid filter UI');

          // Apply frozen columns (React state, not collection)
          this.handleFrozenCol(frozenColumns);

          // Apply collapsed group levels
          // TIMING FIX: Must defer until after Wijmo finishes rebuilding GroupRow objects.
          // endUpdate() triggers an async grid refresh. If we apply collapsed state immediately,
          // we're setting isCollapsed on rows that will be replaced by new GroupRow objects.
          // The 100ms delay allows Wijmo to complete its async grid rebuild.
          console.time('[ViewSwitch] Apply collapsed state');
          if (this.state.grid) {
            setTimeout(() => {
              if (collapsedGroups.length > 0) {
                applyCollapsedGroupLevels(this.state.grid, collapsedGroups);
              } else {
                // If no collapsed groups specified, expand all using optimized method
                this.expandAllGroups(this.state.grid);
              }
              // Trigger re-render so Collapse/Expand button states update
              this.onGroupCollapsedChanged();
              console.timeEnd('[ViewSwitch] Apply collapsed state');
            }, 100);
          }

          // Update view state
          await this.setViewSavedState('')
          await this.setViewState('custom')
          
          console.timeEnd('[ViewSwitch] Total');
        } catch (e) {
          logger.error('[onApplyViewSettings] Error applying view settings:', e);
          console.timeEnd('[ViewSwitch] Total');
        } finally {
          // Hide loading indicator after grid finishes rendering
          this.hideLoadingWhenReady();
        }
      }, 50);
    });
  }

  handleResetAnimation = () => {
    this.setState(prevState => {
      return {
        ...prevState,
        playResetAnimation: true
      }
    })

    setTimeout(() => {
      this.setState(prevState => {
        return {
          ...prevState,
          playResetAnimation: false
        }
      })
    }, 2000)
  }

  onCurrentItemChanged = (currentItem: any) => {
    this.setState({
      currentItem
    });
  };

  toggleMultiSort = () => {
    this.setState(prevState => {
      if (prevState.multiColumnSort) {
        this.collection.sortDescriptions.clear()
      }

      return {
        ...prevState,
        multiColumnSort: !prevState.multiColumnSort
      }
    })
  }

  setExportProgress = (progress: number | null) => {
    this.setState(prevState => ({
      ...prevState,
      exportProgress: progress
    }))
  }

  render() {
    return this.props.children({
      collection: this.collection,
      columnsMapping: this.state.columnsMapping,
      currentItem: this.state.currentItem,
      onRowSelected: this.onRowSelected.bind(this),
      onViewSaved: this.onViewSaved.bind(this),
      onGridInitialized: this.onGridInitialized.bind(this),
      refetch: this.handleRefetch.bind(this),
      selectedItems: this.state.selectedItems,
      status: this.state.status,
      saveStatus: this.state.saveStatus,
      userViewState: this.state.userViewState,
      grid: this.state.grid,
      groups: this.state.groups,
      onFiltersChanged: this.onFiltersChanged.bind(this),
      onExporting: this.onExporting.bind(this),
      onExported: this.onExported.bind(this),
      onInternalGridFiltersChanged: this.onInternalGridFiltersChanged.bind(this),
      filters: this.state.filters,
      defaultColumns: this.columnsMappingDefault,
      // TODO: remaining props to port from GridApiController
      // For now let's only focus on the ones marked as `REQUIRED`
      // --------------------------
      // gridUpdated: this.state.gridUpdated,
      // orderCreateStatus: this.state.orderCreateStatus,
      // orderActiveStatus: this.state.orderActiveStatus,
      // orderRemoveStatus: this.state.orderRemoveStatus,
      // itemAddedStatus: this.state.itemAddedStatus,
      // onGridUpdated: this.onGridUpdated,
      // onOrderCreated: this.onOrderCreated,
      // onOrderActivated: this.onOrderActivated,
      // onOrderRemoved: this.onOrderRemoved,
      // onItemAdded: this.onItemAdded,
      onViewReset: this.onViewReset.bind(this),
      onApplyViewSettings: this.onApplyViewSettings.bind(this),
      // onGridWillUnmount: this.onGridWillUnmount,
      onColSorted: this.onColSorted.bind(this),
      onColResized: this.onColResized.bind(this),
      onColDragged: this.onColDragged.bind(this),
      onColFreezed: this.onColFreezed.bind(this),
      onRowFreezed: this.onRowFreezed.bind(this),
      onGroupCollapsedChanged: this.onGroupCollapsedChanged.bind(this),
      // onNotesSaved: this.onNotesSaved,
      // onItemUpdated: this.onItemUpdated,
      // onCellEditEnded: this.onCellEditEnded,
      onGroupPanelChanged: this.onGroupPanelChanged.bind(this),
      onColumnsMappingChange: this.onColumnsMappingChange.bind(this),
      onCurrentItemChanged: this.onCurrentItemChanged,
      onClearSelection: this.onClearSelection.bind(this),
      // refetching: this.props.refetching,
      hasAllColumnsHidden: this.state.hasAllColumnsHidden,
      frozenColumns: this.state.frozenColumns,
      isLoading: this.state.status === 'loading',
      isCollapseLoading: this.state.isCollapseLoading,
      setCollapseLoading: this.setCollapseLoading,
      // isRefetching: this.props.refetching,
      hasDataToDisplay: this.state.hasDataToDisplay,
      // clearCollectionAndResetChangedOrderItems: this
      //   .clearCollectionAndResetChangedOrderItems,
      playResetAnimation: this.state.playResetAnimation,
      handleResetAnimation: this.handleResetAnimation,
      actionFilters: this.state.actionFilters,
      setFlexGridFilter: this.setFlexGridFilter,
      clearInternalFilters: this.clearInternalFilters,
      toggleMultiSort: this.toggleMultiSort,
      multiColumnSort: this.state.multiColumnSort,
      exportProgress: this.state.exportProgress,
      setExportProgress: this.setExportProgress,
      // Expose method to get current grid filter definition for saving views
      getGridFilterDefinition: this.getGridFilterDefinition,
    })
  }
}
